import { fetchWithTimeout, parseErrorResponse, BULK_FETCH_TIMEOUT } from './base.ts';

/**
 * Bureau afrekenen (#1910). 'nieuw' = nog geen organisatie, 'bestaand' = organisatie die nog
 * niet betaald heeft, 'omzetten' = lopende Professional, 'betaald' = er loopt al een abonnement.
 */
export type BureauMode = 'nieuw' | 'bestaand' | 'omzetten' | 'betaald';
export type BureauInterval = 'annual' | 'monthly';
/** rest = verlengdatum houden, nu = nieuw jaar vanaf vandaag, maandover = van jaar naar maand. */
export type BureauStart = 'rest' | 'nu' | 'maandover';

export interface BureauStatus {
  mode: BureauMode;
  professional: {
    interval: BureauInterval;
    periodEnd: number | null;
    cancelsAtPeriodEnd: boolean;
    /** Wat de klant nu echt betaalt (Stripe), niet de lijstprijs. */
    amountCents: number;
  } | null;
  organization: { id: string; name: string; role: string; paid: boolean } | null;
}

export interface BureauPreview {
  doel: BureauInterval;
  licenties: number;
  currency: string;
  nuBijbetalenCents: number;
  tegoedOverCents: number;
  volgendBedragCents: number;
  /** Unix-seconden; null bij een nieuwe cyclus (die begint vandaag). */
  volgendeDatum: number | null;
  nieuweCyclus: boolean;
}

/** #1916 — het lopende abonnement van de organisatie (Stripe is de waarheid). */
export interface BureauAbonnement {
  organizationId: string;
  rol: 'owner' | 'admin';
  interval: BureauInterval;
  licenties: number;
  bedragCents: number;
  /** Unix-seconden. */
  verlengt: number | null;
  /** Unix-seconden; gezet als er is opgezegd. */
  cancelAt: number | null;
  /** #1919 — waar opzeggen nu op uitkomt (na een verlenging: over een maand). */
  stoptBijOpzeggen: number | null;
  /** #1919 — wat er bij opzeggen meteen terugkomt. */
  terugBijOpzeggenCents: number;
  /** #1919 — wat intrekken kost als er al is terugbetaald. */
  intrekkenKostCents: number;
}

const json = { 'Content-Type': 'application/json' };

export const bureauApi = {
  getBureauAbonnement: async (): Promise<BureauAbonnement | null> => {
    const res = await fetchWithTimeout('/api/bureau/abonnement', {}, BULK_FETCH_TIMEOUT);
    if (!res.ok) await parseErrorResponse(res);
    return (await res.json()).abonnement;
  },

  opzeggenBureau: async (reden: string | null): Promise<BureauAbonnement> => {
    // #1919 — kan terugbetalen en intrekken kan afschrijven: de ruime limiet.
    const res = await fetchWithTimeout(
      '/api/bureau/abonnement/opzeggen',
      { method: 'POST', headers: json, body: JSON.stringify({ reden }) },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return (await res.json()).abonnement;
  },

  intrekkenBureau: async (betaal = false): Promise<BureauAbonnement> => {
    const res = await fetchWithTimeout(
      '/api/bureau/abonnement/intrekken',
      { method: 'POST', headers: json, body: JSON.stringify({ betaal }) },
      BULK_FETCH_TIMEOUT
    );
    if (!res.ok) await parseErrorResponse(res);
    return (await res.json()).abonnement;
  },

  getBureauStatus: async (): Promise<BureauStatus> => {
    const res = await fetchWithTimeout('/api/bureau/status');
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /** Geeft de Stripe-URL waar de klant afrekent. */
  startBureauCheckout: async (data: {
    interval: BureauInterval;
    seats?: number;
    name?: string;
    slug?: string;
    organizationId?: string;
    kortingscode?: string;
  }): Promise<{ url: string }> => {
    const res = await fetchWithTimeout('/api/bureau/checkout', {
      method: 'POST',
      headers: json,
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  /** Terugkeer uit Stripe: rondt af als de webhook er nog niet was. */
  verifyBureauCheckout: async (
    sessionId: string
  ): Promise<{ status: 'klaar' | 'wacht' | 'aandacht'; organizationId?: string }> => {
    const res = await fetchWithTimeout(
      `/api/bureau/checkout/verify?session_id=${encodeURIComponent(sessionId)}`
    );
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  previewBureauConversion: async (data: {
    seats?: number;
    start: BureauStart;
  }): Promise<BureauPreview> => {
    const res = await fetchWithTimeout('/api/bureau/convert/preview', {
      method: 'POST',
      headers: json,
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },

  convertToBureau: async (data: {
    name: string;
    slug: string;
    seats?: number;
    start: BureauStart;
  }): Promise<{ organizationId: string }> => {
    const res = await fetchWithTimeout('/api/bureau/convert', {
      method: 'POST',
      headers: json,
      body: JSON.stringify(data),
    });
    if (!res.ok) await parseErrorResponse(res);
    return res.json();
  },
};
